import {
  getApiDebuggingId,
  getApiHttpStatus,
  getErrorKind,
  getSurfaceableApiDetail,
  isApiErrorResponse,
} from 'helpers/rtk-utils';

const DEFAULT_EXPORT_ERROR_MESSAGE = 'Export failed. Please try again.';

const SURFACEABLE_EXPORT_ERROR_STATUSES = [400, 503];

// Bounded because the download route's 500 body interpolates the raw exception, and a SQLAlchemy
// failure stringifies to the driver message plus the full SQL statement and its bind values.
const MAX_ERROR_SUMMARY_LENGTH = 200;

export class ExportUserError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExportUserError';
    Object.setPrototypeOf(this, ExportUserError.prototype);
  }
}

// Any 400 or 503 detail raised by POST /admin/donation/download_request or
// POST /admin/event-ticket/download_request reaches a partner verbatim.
export const getSurfaceableExportDetail = (err: unknown): string | undefined =>
  getSurfaceableApiDetail(err, SURFACEABLE_EXPORT_ERROR_STATUSES);

export const getExportErrorMessage = (err: unknown): string => {
  const detail = getSurfaceableExportDetail(err);
  if (detail !== undefined) {
    return detail;
  }

  if (err instanceof ExportUserError) {
    return err.message;
  }

  return DEFAULT_EXPORT_ERROR_MESSAGE;
};

const readErrorSummary = (err: unknown): string | undefined => {
  if (isApiErrorResponse(err)) {
    if (typeof err.data === 'string') return err.data;
    if (typeof err.data === 'object' && err.data !== null) {
      const { detail } = err.data as { detail?: unknown };
      if (typeof detail === 'string' && detail.trim() !== '') return detail;
    }
    if (err.error !== undefined) return String(err.error);
  }
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message?: unknown }).message);
  }
  return undefined;
};

const summarizeExportError = (err: unknown): string | undefined => {
  const summary = readErrorSummary(err);
  if (summary === undefined) return undefined;
  return summary.length > MAX_ERROR_SUMMARY_LENGTH
    ? `${summary.slice(0, MAX_ERROR_SUMMARY_LENGTH)}…`
    : summary;
};

export type ExportFailure = {
  message: string;
  severity: 'info' | 'error';
  logMessage: string;
  logContext: Record<string, unknown>;
};

export const describeExportFailure = (err: unknown): ExportFailure => {
  const message = getExportErrorMessage(err);
  const guardrailDetail = getSurfaceableExportDetail(err);
  const correlationId = getApiDebuggingId(err);

  if (guardrailDetail !== undefined) {
    return {
      message,
      severity: 'info',
      logMessage: 'Report export rejected by a guardrail',
      logContext: {
        httpStatus: getApiHttpStatus(err),
        guardrailDetail,
        // snake_case deliberately: matches the backend's correlation_id facet so one query
        // joins a browser log to its backend log. Don't camelCase it.
        correlation_id: correlationId,
      },
    };
  }

  return {
    message,
    severity: 'error',
    logMessage: 'Report export failed',
    logContext: {
      httpStatus: getApiHttpStatus(err),
      errorKind: getErrorKind(err),
      errorSummary: summarizeExportError(err),
      correlation_id: correlationId,
    },
  };
};
